import { Radio, RadioGroup } from '@headlessui/react';
import clsx from 'clsx';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import {
  enterpriseTier,
  freeTier,
  licenseTypeOptions,
  proTier,
  starterTier,
} from '../../utils/pricing/tiers';
import type { PlanSeats, PriceVariables } from '../../utils/pricing/types';
import Section from '../marketing/Section';

import { Discount } from './Discount';
import { Price } from './Price';
import { PricingFeatures } from './PricingFeatures';

const seatsLabels: Record<PlanSeats, string> = {
  1: '1 user',
  3: '3 users',
  10: '10 users',
};

export default function PricingTable() {
  const [licenseType, setLicenseType] = useState(licenseTypeOptions[1]);
  const [selectedUserPlan, setSelectedUserPlan] = useState<PlanSeats>(10);

  const variables: PriceVariables = {
    licenseType: licenseType.value,
    userPlan: selectedUserPlan,
  };

  const tiers = useMemo(() => {
    if (licenseType.value === 'individual') {
      return [freeTier];
    } else {
      return [starterTier, proTier, enterpriseTier];
    }
  }, [licenseType]);

  return (
    <div className="bg-white pb-5">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <Section title="Pricing" isWhite />
          <p className="-mt-4 mx-auto max-w-3xl text-pretty text-center text-lg font-semibold text-neutral-900 sm:text-xl/8">
            Find a plan which fits your needs
          </p>
        </div>
        <div className="mt-16 flex justify-center">
          <fieldset aria-label="License type">
            <RadioGroup
              value={licenseType}
              onChange={(type) => {
                setLicenseType(type);
              }}
            >
              <div className="grid grid-cols-3 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-neutral-200">
                {licenseTypeOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option}
                    className="cursor-pointer rounded-full px-2.5 py-1 text-neutral-500 data-[checked]:bg-primary-600 data-[checked]:text-white"
                  >
                    {option.label}
                  </Radio>
                ))}
              </div>
            </RadioGroup>
          </fieldset>
        </div>

        <div className="isolate mx-auto mt-28 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => {
            const price = tier.price(variables);
            const cta = tier.cta(variables);

            return (
              <div
                key={tier.id}
                className={clsx(
                  tier.featured
                    ? 'bg-neutral-900 ring-neutral-900'
                    : 'ring-neutral-200',
                  'rounded-3xl p-8 ring-1 xl:p-10 relative',
                  tiers.length === 1 && 'lg:col-start-2',
                )}
              >
                {tier.seats(variables) && (
                  <div
                    className={clsx(
                      'absolute left-0 -top-10 w-full justify-center items-center flex flex-1',
                      price.firstYearDiscount ? '-top-18' : '-top-12',
                    )}
                  >
                    <RadioGroup
                      value={selectedUserPlan}
                      onChange={setSelectedUserPlan}
                    >
                      <div className="grid grid-cols-3 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-neutral-200">
                        {Object.entries(seatsLabels).map(([key, value]) => {
                          return (
                            <Radio
                              key={key}
                              value={Number(key)}
                              className="cursor-pointer rounded-full px-2.5 py-1 text-neutral-500 data-[checked]:bg-primary-600 data-[checked]:text-white"
                            >
                              {value}
                            </Radio>
                          );
                        })}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                <div className="flex justify-between items-baseline gap-1">
                  <h3
                    id={tier.id}
                    className={clsx(
                      tier.featured ? 'text-white' : 'text-neutral-900',
                      'text-lg/8 font-semibold',
                    )}
                  >
                    {tier.name}
                  </h3>
                  <h4 className="text-neutral-400 whitespace-nowrap">
                    {tier.type(variables)}
                  </h4>
                </div>
                <div
                  className={clsx(
                    tier.featured ? 'text-neutral-300' : 'text-neutral-600',
                    'mt-4 text-sm/6 min-h-18',
                  )}
                >
                  {tier.description}
                </div>
                <Price price={price} tier={tier} />
                <Discount price={price} />
                <Link
                  href={cta.href}
                  aria-describedby={tier.id}
                  className={clsx(
                    'mt-6 block rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline-2 focus-visible:outline-offset-2',
                    {
                      'bg-white/10 text-white hover:bg-white/20 focus-visible:outline-white':
                        tier.featured,
                      'bg-primary-600 text-white shadow-sm hover:bg-primary-500 focus-visible:outline-primary-600':
                        !cta.soft && !tier.featured,
                      'bg-neutral-700 text-white hover:bg-neutral-800 focus-visible:outline-neutral-700':
                        cta.soft && !tier.featured,
                    },
                  )}
                >
                  {cta.value}
                </Link>

                <PricingFeatures tier={tier} variables={variables} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
